import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useStoreActions } from "easy-peasy";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";

import logo from "../../../assets/images/svg/logo-flex-sacarabany-sarl.svg";

import "./css/pdf.css";

// --------------------- UTILS ---------------------
import {
  dateFormatted,
  formatDateTime,
  getQuantityDisplay,
  renderOrDash,
  getTasksFromDescription,
} from "../../tools/manager";

import base64 from "react-native-base64";

// base publique pour les documents (à configurer dans .env)
const DOCUMENTS_BASE_URL =
  process.env.REACT_APP_PUBLIC_DOCUMENTS_URL || "";

// construit une URL à partir du key S3
const makeDocUrl = (doc) => {
  if (!DOCUMENTS_BASE_URL) return null;
  if (!doc?.key) return null;

  // côté mobile, tu stockes sous "documents/<key>" → on reproduit
  const hasDocumentsPrefix = doc.key.startsWith("documents/");
  const keyPath = hasDocumentsPrefix ? doc.key : `documents/${doc.key}`;

  return `${DOCUMENTS_BASE_URL.replace(/\/$/, "")}/${keyPath}`;
};

// --------------------- MAIN COMPONENT ---------------------
const PdfWorkOrders = () => {
  const { idworkorder } = useParams();
  const navigate = useNavigate();

  // 🌱 Store actions
  const getInterventionCoupon = useStoreActions(
    (actions) => actions.middleware.OrdersMiddleware.getInterventionCoupon
  );
  const getUser = useStoreActions(
    (actions) => actions.middleware.AdminMiddleware.getAdmin
  );
  const getInterventionDocuments = useStoreActions(
    (actions) =>
      actions.middleware.OrdersMiddleware.interventionDocumentsByIntervention
  );
  const getSignedUrlS3 = useStoreActions((actions) => actions.middleware.FileS3.getSignedUrlS3);

  // States
  const [dataPdf, setDataPdf] = useState(null);
  const [signatureSvg, setSignatureSvg] = useState("");
  const [materialsInd, setMaterialsInd] = useState([]);
  const [materialsComp, setMaterialsComp] = useState([]);
  const [userName, setUserName] = useState("");
  const [documents, setDocuments] = useState([]);

  // Ref pour impression
  const componentRef = useRef();

  const handlePdf = useReactToPrint({
    contentRef: componentRef,
    content: () => componentRef.current,
    documentTitle: `bon-intervention-${idworkorder}`,
    onAfterPrint: () => toast.success("✅ PDF généré avec succès"),
  });

  // Fetch data
  const fetchInterventionCoupon = useCallback(async () => {
    try {
      const res = await getInterventionCoupon(idworkorder);
      const materialData = res.materialData?.items || [];

      // Si "intervention urgent" faite au nom d'un user spécifique
      if (
        res &&
        res?.name &&
        res?.name === "intervention urgent" &&
        res?.interventionCouponUserId
      ) {
        const resUser = await getUser(res?.interventionCouponUserId);
        setUserName(
          (resUser?.first_name || "") + " " + (resUser?.last_name || "")
        );
      } else {
        setUserName(res?.name || "");
      }

      // Matériel principal (avec indexRef)
      setMaterialsInd(materialData.filter((m) => m.indexRef != null));

      // Matériel complémentaire (sans indexRef et sans QR)
      setMaterialsComp(
        materialData.filter(
          (m) => m.indexRef == null && m.referenceQr == null
        )
      );

      setDataPdf(res);
      setSignatureSvg(res?.signed ? base64.decode(res.signed) : "");

      // 🔹 Récupération des documents d'intervention (photos avant/après)
      if (res?.id) {
        try {
          let allDocs = [];
          let nextToken = null;

          // 1. Récupérer tous les documents via la query paginée
          do {
            const { items, nextToken: nt } =
              (await getInterventionDocuments({
                interventionCouponId: res.id,
                limit: 50,
                nextToken,
              })) || {};
            if (items?.length) {
              allDocs = allDocs.concat(items);
            }
            nextToken = nt;
          } while (nextToken);

          // 2. Générer les URL signées S3 pour chaque document
          const docsWithUrls = await Promise.all(
            allDocs.map(async (doc) => {
              try {
                if (!doc?.key) return { ...doc, url: null };

                const keyPath = `${doc.key}`;

                const signedUrl = await getSignedUrlS3(keyPath);
                console.log("URL :", signedUrl);

                return {
                  ...doc,
                  url: signedUrl || null,
                };
              } catch (e) {
                console.error("Erreur getSignedUrlS3 pour doc", doc?.id, e);
                return { ...doc, url: null };
              }
            })
          );

          setDocuments(docsWithUrls);
        } catch (e) {
          console.error(
            "Erreur chargement documents intervention :",
            e
          );
        }
      }
    } catch (error) {
      console.error("Error: ", error);
      toast.error("❌ Erreur lors du chargement du bon d’intervention");
    }
  }, [getInterventionCoupon, getUser, getInterventionDocuments, idworkorder]);

  useEffect(() => {
    fetchInterventionCoupon();
  }, [fetchInterventionCoupon]);

  if (!dataPdf) return <p className="p-10">Chargement...</p>;

  const dateWorksiteStart = dateFormatted(dataPdf?.worksite?.date_planned);

  // ========= COLONNES OPTIONNELLES (surface / lieux) =========
  const hasDepot = materialsInd.some(
    (m) =>
      m?.address !== null &&
      m?.address !== undefined &&
      String(m.address).trim() !== ""
  );
  const hasPose = materialsInd.some(
    (m) =>
      m?.poseAddress !== null &&
      m?.poseAddress !== undefined &&
      String(m.poseAddress).trim() !== ""
  );
  const hasSurface = materialsInd.some(
    (m) =>
      m?.surface !== null &&
      m?.surface !== undefined &&
      String(m.surface).trim() !== ""
  );

  const mainColsCount =
    4 + (hasDepot ? 1 : 0) + (hasPose ? 1 : 0) + (hasSurface ? 1 : 0);
  // Nature, Matériel, Référence, Quantité = 4

  // ========= HORAIRES (startDate / endDate / durée) =========
  const startDateFormatted = dataPdf?.startDate
    ? formatDateTime(dataPdf.startDate)
    : null;
  const endDateFormatted = dataPdf?.endDate
    ? formatDateTime(dataPdf.endDate)
    : null;

  let durationStr = "";
  if (dataPdf?.startDate && dataPdf?.endDate) {
    const start = new Date(dataPdf.startDate);
    const end = new Date(dataPdf.endDate);
    const diffMs = end.getTime() - start.getTime();
    if (diffMs > 0) {
      const totalMin = Math.round(diffMs / 60000);
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      durationStr = `${h ? `${h}h` : ""}${h && m ? " " : ""}${m ? `${m}min` : ""
        }`;
    }
  }

  // ========= EXTRA TECHNICIANS =========
  let extraTechnicians = [];
  if (dataPdf?.extraTechnicians) {
    if (typeof dataPdf.extraTechnicians === "string") {
      try {
        extraTechnicians = JSON.parse(dataPdf.extraTechnicians);
      } catch (e) {
        console.error("Erreur parse extraTechnicians :", e);
      }
    } else if (Array.isArray(dataPdf.extraTechnicians)) {
      extraTechnicians = dataPdf.extraTechnicians;
    }
  }

  const renderExtraTechName = (t) => {
    const first = (t.first_name || t.firstName || "").trim();
    const last = (t.last_name || t.lastName || "").trim();
    const label = `${first} ${last}`.trim();
    return label || "Technicien sans nom";
  };

  // ========= DOCUMENTS (photos avant / après) =========
  const beforeDocs = documents.filter((d) => d.before);
  const afterDocs = documents.filter((d) => d.after);

  // --------------------- RENDER HELPERS ---------------------
  const renderMaterialRow = (m, index) => {
    const references =
      m?.materialSettings?.references?.[0]?.reference?.split(",") || [];
    const ref =
      m.indexRef != null && references[m.indexRef] !== undefined
        ? references[m.indexRef]
        : "-";

    return (
      <tr key={index} className="text-sm text-gray-file">
        <td>{renderOrDash(m?.typeInterventionSettings?.name)}</td>
        <td>{renderOrDash(m?.materialSettings?.name)}</td>
        <td>{renderOrDash(ref)}</td>
        {hasDepot && <td>{renderOrDash(m?.address)}</td>}
        {hasPose && <td>{renderOrDash(m?.poseAddress)}</td>}
        {hasSurface && <td>{renderOrDash(m?.surface)}</td>}
        <td>{getQuantityDisplay(m)}</td>
      </tr>
    );
  };

  const renderComplementaryRow = (m, index) => {
    const { isMulti, tasks, raw } = getTasksFromDescription(m?.description);

    let descriptionNode;

    if (isMulti && Array.isArray(tasks) && tasks.length > 0) {
      descriptionNode = (
        <ul style={{ paddingLeft: 16, margin: 0 }}>
          {tasks.map((t, i) => {
            const label =
              typeof t === "string"
                ? t.trim()
                : (t?.label || "").trim();

            const qty =
              typeof t === "string"
                ? ""
                : (t?.quantityLabel || "").trim();

            if (!label && !qty) return null;

            const base = `${i + 1}. ${label || "-"}`;

            const content = qty ? (
              <>
                {base} -{" "}
                <span className="font-extrabold">Qté : {qty}</span>
              </>
            ) : (
              base
            );

            return <li key={i}>{content}</li>;
          })}
        </ul>
      );
    } else {
      descriptionNode = renderOrDash(raw);
    }

    return (
      <tr key={index} className="text-sm text-gray-file">
        <td>{renderOrDash(m?.name)}</td>
        <td>{descriptionNode}</td>
      </tr>
    );
  };

  // --------------------- JSX ---------------------
  return (
    <div>
      <section className="ml-32 mt-14 mb-10 flex space-x-7">
        {/* DOCUMENT */}
        <div
          ref={componentRef}
          className="pdf-page w-[827px] min-h-[1170px] shadow-2xl rounded-lg bg-white"
        >
          {/* Header Bar */}
          <div className="bg-orange-clim rounded-t-lg h-4" />

          <div className="flex flex-col mt-10 px-10">
            {/* Logo + Title */}
            <div className="flex items-center justify-between mb-6">
              <img src={logo} alt="Logo Sacarabany" className="h-12" />
              <h3 className="font-bold text-lg">
                Bon d'intervention - N° {dataPdf?.numero}
              </h3>
            </div>

            {/* Infos */}
            <div className="flex justify-between mt-5">
              {/* Infos société */}
              <div className="mr-28 flex flex-col space-y-2">
                <h4>SACARABANY SARL</h4>
                <div className="text-sm text-gray-file space-y-1">
                  <p>279 rue solitude la mulatresse</p>
                  <p>97200 Fort de France</p>
                  <p>SIRET : 495 262 131 00016</p>
                  <p>contact@sacarabany-sarl.fr</p>
                  <p>Fait par : {userName}</p>
                  <p>Le : {formatDateTime(dataPdf?.createdAt)}</p>
                </div>
              </div>

              {/* Infos client */}
              <div className="flex flex-col space-y-2 text-sm">
                <h4>
                  {dataPdf?.worksite?.company?.name?.toUpperCase() ||
                    dataPdf?.nameClient}
                </h4>
                <div className="text-gray-file space-y-1">
                  <p>{dataPdf?.worksite?.company?.address}</p>
                  {dataPdf?.worksite?.company?.siret && (
                    <p>SIRET : {dataPdf?.worksite?.company?.siret}</p>
                  )}
                  <p>
                    {dataPdf?.newMailClient ||
                      dataPdf?.worksite?.company?.mail?.split(",")[0] ||
                      ""}
                  </p>
                  <p>{dataPdf?.worksite?.company?.phone || ""}</p>
                </div>
              </div>
            </div>

            {/* Chantier + infos BC */}
            <div className="mt-16 space-y-2">
              <h4>
                {dataPdf?.worksite?.name || dataPdf?.nameWorksite}{" "}
                {dataPdf?.worksite &&
                  `- ${dateWorksiteStart.day}/${dateWorksiteStart.month}/${dateWorksiteStart.year}`}
              </h4>

              {(dataPdf?.orderSelected?.numero ||
                dataPdf?.orderCoupon?.numero ||
                dataPdf?.bcCtm) && (
                  <h4>
                    Bon de commande{" "}
                    {dataPdf?.bcCtm || dataPdf?.preInterventionMode
                      ? "ctm "
                      : ""}
                    N°: {dataPdf?.orderSelected?.numero || ""}{" "}
                    {dataPdf?.orderCoupon?.numero || ""}{" "}
                    {dataPdf?.bcCtm || ""}
                  </h4>
                )}

              {/* Horaires de l'intervention */}
              {(startDateFormatted || endDateFormatted || durationStr) && (
                <div className="mt-4 space-y-1 text-sm text-gray-file">
                  <h4 className="font-semibold text-base text-gray-800">
                    Horaires de l'intervention
                  </h4>
                  {startDateFormatted && (
                    <p>Heure d'arrivée : {startDateFormatted}</p>
                  )}
                  {endDateFormatted && (
                    <p>Heure de départ : {endDateFormatted}</p>
                  )}
                  {durationStr && (
                    <p>Durée totale : {durationStr}</p>
                  )}
                </div>
              )}

              {/* Autres techniciens intervenus */}
              {extraTechnicians.length > 0 && (
                <div className="mt-4 text-sm text-gray-file">
                  <h4 className="font-semibold text-base text-gray-800 mb-1">
                    Autres techniciens intervenus
                  </h4>
                  <ul className="list-disc list-inside space-y-0.5">
                    {extraTechnicians.map((t, idx) => (
                      <li key={t.id || idx}>
                        {renderExtraTechName(t)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Table matériels principaux */}
              {materialsInd.length > 0 && (
                <>
                  <h4 className="py-8">
                    Etat des lieux de l’intervention
                  </h4>

                  <table className="tableContainer">
                    <thead>
                      <tr>
                        <th>Nature de l'intervention</th>
                        <th>Dénomination du matériel</th>
                        <th>Référence</th>
                        {hasDepot && <th>Lieu de dépose</th>}
                        {hasPose && <th>Lieu de pose</th>}
                        {hasSurface && <th>Surface</th>}
                        <th>Quantité</th>
                      </tr>
                    </thead>
                    <tbody>{materialsInd.map(renderMaterialRow)}</tbody>
                  </table>
                </>
              )}
            </div>

            {/* Complémentaires */}
            {materialsComp.length > 0 && (
              <div className="mt-16 pb-6 space-y-4">
                <h4>Intervention.s complémentaire.s</h4>
                <table className="tableContainer">
                  <thead>
                    <tr>
                      <th>Dénomination de l’intervention</th>
                      <th>Référence</th>
                    </tr>
                  </thead>
                  <tbody>{materialsComp.map(renderComplementaryRow)}</tbody>
                </table>
              </div>
            )}

            {/* Photos avant / après */}
            {(beforeDocs.length > 0 || afterDocs.length > 0) && (
              <div className="mt-8 pb-6 space-y-4">
                <h4>Photos de l’intervention</h4>

                {beforeDocs.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Avant intervention
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {beforeDocs.map((doc) => (
                        <div
                          key={doc.id}
                          className="w-[46%] border border-gray-200 rounded-md p-2"
                        >
                          {doc?.url ? (
                            <img
                              src={doc?.url}
                              alt={doc.comment || "Photo avant intervention"}
                              className="w-full h-32 object-cover rounded"
                            />
                          ) : (
                            <p className="text-xs text-gray-400 break-all">
                              {doc.key}
                            </p>
                          )}
                          {doc.comment && (
                            <p className="mt-1 text-xs text-gray-500">
                              {doc.comment}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {afterDocs.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mt-3 mb-1">
                      Après intervention
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {afterDocs.map((doc) => (
                        <div
                          key={doc.id}
                          className="w-[46%] border border-gray-200 rounded-md p-2"
                        >
                          {doc?.url ? (
                            <img
                              src={doc?.url}
                              alt={doc.comment || "Photo après intervention"}
                              className="w-full h-32 object-cover rounded"
                            />
                          ) : (
                            <p className="text-xs text-gray-400 break-all">
                              {doc.key}
                            </p>
                          )}
                          {doc.comment && (
                            <p className="mt-1 text-xs text-gray-500">
                              {doc.comment}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Signature */}
            <div className="grow" />
            <div className="text-right flex flex-col items-end justify-end">
              <p className="font-medium px-11">Signature :</p>
              {!!signatureSvg && (
                <div
                  className="bg-cover"
                  dangerouslySetInnerHTML={{ __html: signatureSvg }}
                />
              )}
            </div>

            {/* Footer */}
            <footer className="text-center text-gray-file text-xs border-t border-gray-separate pt-5 pb-7">
              <h6>
                SACARABANY SARL SIRET : 495 262 131 00016 - APE : 453 A
              </h6>
              <p>279 rue solitude la mulatresse - 97200 Fort de France</p>
              <p>TEL : 0596 70 75 50 - 0696 45 02 08</p>
            </footer>
          </div>
        </div>

        {/* ACTIONS */}
        <div className="flex flex-col justify-between text-blue-admin space-y-4">
          <button
            onClick={() => navigate(-1)}
            className="border border-blue-admin py-4 px-3 rounded-xl"
          >
            Voir tous les bons de commandes
          </button>

          <button
            className="bg-blue-admin text-white py-4 px-6 rounded-xl"
            onClick={handlePdf}
          >
            Générer un PDF
          </button>
        </div>
      </section>
    </div>
  );
};

export default PdfWorkOrders;
